import { useState, useEffect, useCallback } from "react";
import { getExchangeRate } from "../services/api";
import type { IExchangeRateResponse } from "../services/api";
import { useStore } from "../store/globalState";
import type { IExchangeRate } from "../interfaces/transactions";

/**
 * Country to currency mapping
 */
export const COUNTRY_CURRENCY: Record<string, string> = {
  US: "USD",
  MX: "MXN",
  AR: "ARS",
  CO: "COP",
  BR: "BRL",
  ES: "EUR",
  DE: "EUR",
  FR: "EUR",
};

interface UseExchangeRateResult {
  rate: number | null;
  data: IExchangeRateResponse | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch exchange rate between two currencies
 * @param sourceCurrency - Source currency code (e.g., 'USD')
 * @param destinationCurrency - Destination currency code (e.g., 'MXN')
 */
export const useExchangeRate = (
  sourceCurrency: string,
  destinationCurrency: string
): UseExchangeRateResult => {
  const { setExchangerate } = useStore();
  const [data, setData] = useState<IExchangeRateResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchRate = useCallback(async () => {
    if (!sourceCurrency || !destinationCurrency) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    // Read the cached rate from the store at call time so that storing a new
    // rate does not re-create this callback and re-run the effect below.
    const cachedRate = useStore.getState().exchangeRate;
    if (!cachedRate) {
      try {
        const result = await getExchangeRate(
          sourceCurrency,
          destinationCurrency
        );
        setData(result);
        setExchangerate(result as unknown as IExchangeRate);
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to fetch exchange rate")
        );
      } finally {
        setLoading(false);
      }
    } else setLoading(false);
  }, [sourceCurrency, destinationCurrency, setExchangerate]);

  useEffect(() => {
    fetchRate();
  }, [fetchRate]);

  return {
    rate: data?.rate ?? null,
    data,
    loading,
    error,
    refetch: fetchRate,
  };
};
